import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  useMediaQuery,
  Icon,
  Badge,
  Spinner,
  Image,
  useToast,
} from "@chakra-ui/react";
import { HamburgerIcon, SearchIcon, CloseIcon } from "@chakra-ui/icons";
import {
  FaShoppingCart,
  FaUserCircle,
  FaBook,
  FaHome,
  FaPhoneAlt,
  FaSignOutAlt,
  FaUser,
  FaGavel,
  FaSearch,
} from "react-icons/fa";
import { RiInboxUnarchiveFill } from "react-icons/ri";
import { MdOutlineAdminPanelSettings } from "react-icons/md";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Authcontext } from "../Context/Authcontext";
import { CartContext } from "../Context/CartContext";
import { apiUrl } from "../ApiUrl/ApiUrl";

const Navbar = () => {
  const { cartData = [], clearCart } = useContext(CartContext);
  const { correct, logout } = useContext(Authcontext);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLargerThan768] = useMediaQuery("(min-width: 768px)");
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  // Search state
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchContainerRef = useRef(null);

  // User state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState("");

  const checkLoginStatus = () => {
    const isAuth = localStorage.getItem("loginCheck") === "true";
    const token = localStorage.getItem("token");
    const uid = localStorage.getItem("uid");
    if (isAuth && token && uid) {
      const storedName = localStorage.getItem("loginsetName");
      setUsername(storedName || "User");
      setIsLoggedIn(true);
    } else {
      setIsLoggedIn(false);
      setUsername("");
    }
  };

  useEffect(() => {
    checkLoginStatus();
  }, [correct]);

  // Live search debounce
  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        let response = await fetch(`${apiUrl}/public/search?keyword=${encodeURIComponent(query.trim())}`);
        if (!response.ok) {
          response = await fetch(`${apiUrl}/public/filter?keyword=${encodeURIComponent(query.trim())}`);
        }
        if (response.ok) {
          const data = await response.json();
          setResults(Array.isArray(data) ? data.slice(0, 8) : []);
        } else {
          setResults([]);
        }
      } catch (error) {
        console.error("Error fetching search results:", error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(fetchData, 280);
    return () => clearTimeout(debounceTimer);
  }, [query]);

  // Close search suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setIsSearchFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInput = (e) => {
    setQuery(e.target.value);
    setIsSearchFocused(true);
  };

  const handleExecuteSearch = (searchVal) => {
    const term = (searchVal !== undefined ? searchVal : query).trim();
    if (!term) return;
    setIsSearchFocused(false);
    localStorage.removeItem("subcatname");
    localStorage.removeItem("maincatname");
    localStorage.removeItem("selectedLanguage");
    navigate(`/products?keyword=${encodeURIComponent(term)}`);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleExecuteSearch();
    }
  };

  const handleSelectResult = (item) => {
    setIsSearchFocused(false);
    setQuery("");
    navigate(`/products/view/${item.id}`);
  };

  const handleLogout = () => {
    if (logout) logout();
    if (clearCart) clearCart();
    checkLoginStatus();
    toast({
      title: "Signed Out Successfully",
      description: "Your session and cart items have been cleared.",
      status: "info",
      duration: 2500,
      isClosable: true,
      position: "top",
    });
    if (
      location.pathname === "/myprofile" ||
      location.pathname === "/orderpage" ||
      location.pathname === "/cart" ||
      location.pathname === "/delivery" ||
      location.pathname === "/summary" ||
      location.pathname === "/payment" ||
      location.pathname.startsWith("/admin")
    ) {
      navigate("/");
    }
  };

  const role = localStorage.getItem("role");
  const isAdminOrStaff = role === "ADMIN" || role === "staff";

  return (
    <Box position="sticky" top="0" zIndex="1000" w="100%" bg="#0F172A">
      {/* Top Utility Announcement Bar */}
      <Box bg="#0B132B" py="1.5" px="4" borderBottom="1px solid rgba(255,255,255,0.07)">
        <Flex maxW="1360px" mx="auto" justify="space-between" align="center" fontSize="12px" color="gray.300">
          <Flex align="center" gap="2">
            <Badge colorScheme="yellow" variant="subtle" fontSize="10px" px="1.5" py="0.5" rounded="full">
              PREMIUM
            </Badge>
            <Text display={{ base: "none", sm: "inline" }}>
              India's Premier Legal Literature & Law Technology Marketplace
            </Text>
            <Text display={{ base: "inline", sm: "none" }}>
              Navneet Law Studios
            </Text>
          </Flex>
          <Flex align="center" gap="4">
            <Link to="/contact">
              <Text _hover={{ color: "yellow.400" }} transition="color 0.2s">Support: +91 9765010530</Text>
            </Link>
            <Link to="/about">
              <Text display={{ base: "none", md: "inline" }} _hover={{ color: "yellow.400" }} transition="color 0.2s">About Us</Text>
            </Link>
          </Flex>
        </Flex>
      </Box>

      {/* Main Navbar */}
      <Box
        bg="rgba(15, 23, 42, 0.96)"
        backdropFilter="blur(12px)"
        borderBottom="1px solid rgba(255, 255, 255, 0.1)"
        py={{ base: "2.5", md: "3" }}
        px={{ base: "3", md: "6", lg: "8" }}
      >
        <Flex maxW="1360px" mx="auto" align="center" justify="space-between" gap={{ base: 2, md: 4 }}>
          {/* Mobile Menu Button */}
          {!isLargerThan768 && (
            <Button
              variant="ghost"
              color="white"
              p="1"
              minW="auto"
              _hover={{ bg: "whiteAlpha.200" }}
              onClick={onOpen}
              aria-label="Open Navigation"
            >
              <HamburgerIcon boxSize="22px" />
            </Button>
          )}

          {/* Brand Logo */}
          <NavLink to="/">
            <Flex align="center" gap="2.5" cursor="pointer">
              <Box
                p="2"
                bg="linear-gradient(135deg, #EAB308 0%, #CA8A04 100%)"
                rounded="lg"
                color="gray.950"
                shadow="0 2px 8px rgba(234, 179, 8, 0.3)"
              >
                <Icon as={FaGavel} boxSize="18px" />
              </Box>
              <Box>
                <Flex align="baseline">
                  <Text
                    fontSize={{ base: "17px", md: "20px" }}
                    fontWeight="800"
                    letterSpacing="-0.5px"
                    color="white"
                    lineHeight="1.1"
                  >
                    Navneet Law
                  </Text>
                  <Text
                    fontSize={{ base: "17px", md: "20px" }}
                    fontWeight="800"
                    color="yellow.400"
                    ml="1"
                    lineHeight="1.1"
                  >
                    Studios
                  </Text>
                </Flex>
                <Text
                  fontSize="9px"
                  fontWeight="600"
                  letterSpacing="0.8px"
                  textTransform="uppercase"
                  color="gray.400"
                  display={{ base: "none", sm: "block" }}
                >
                  Legal Literature & AI Tech
                </Text>
              </Box>
            </Flex>
          </NavLink>

          {/* Desktop Search Bar with Live Suggestions Dropdown */}
          {isLargerThan768 && (
            <Box ref={searchContainerRef} flex="1" maxW="600px" mx="4" position="relative">
              <InputGroup size="md">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search legal books, bare acts, authors, software..."
                  value={query}
                  onChange={handleInput}
                  onFocus={() => setIsSearchFocused(true)}
                  onKeyDown={handleKeyDown}
                  bg="rgba(255, 255, 255, 0.08)"
                  border="1px solid rgba(255, 255, 255, 0.15)"
                  color="white"
                  fontSize="13.5px"
                  rounded="full"
                  px="4"
                  _placeholder={{ color: "gray.400" }}
                  _hover={{ borderColor: "rgba(234, 179, 8, 0.4)", bg: "rgba(255, 255, 255, 0.12)" }}
                  _focus={{
                    borderColor: "yellow.400",
                    bg: "rgba(15, 23, 42, 0.98)",
                    boxShadow: "0 0 0 1px #EAB308, 0 4px 16px rgba(0,0,0,0.4)",
                  }}
                  transition="all 0.2s"
                />
                <InputRightElement>
                  {loading ? (
                    <Spinner size="xs" color="yellow.400" />
                  ) : query ? (
                    <CloseIcon
                      boxSize="10px"
                      color="gray.400"
                      cursor="pointer"
                      onClick={() => setQuery("")}
                      _hover={{ color: "white" }}
                    />
                  ) : null}
                </InputRightElement>
              </InputGroup>

              {/* Live Search Suggestions Dropdown */}
              {isSearchFocused && results.length > 0 && (
                <Box
                  position="absolute"
                  top="calc(100% + 8px)"
                  left="0"
                  right="0"
                  bg="#1E293B"
                  border="1px solid rgba(255, 255, 255, 0.12)"
                  rounded="xl"
                  shadow="0 16px 36px rgba(0, 0, 0, 0.45)"
                  py="2"
                  zIndex="1100"
                  overflow="hidden"
                >
                  <Text px="4" py="1.5" fontSize="11px" fontWeight="bold" textTransform="uppercase" color="gray.400">
                    Search Results ({results.length})
                  </Text>
                  {results.map((item) => {
                    const itemTitle = item.booktitle || item.bookTitle || item.name || item.title || "Legal Book";
                    const itemAuthor = item.author ? `By ${item.author}` : item.subcatname || item.maincatname || "";
                    const itemPrice = item.descprice || item.price || item.originalprice;
                    const itemImg = item.imagePath || item.image || item.image1;
                    return (
                      <Flex
                        key={item.id}
                        px="4"
                        py="2.5"
                        align="center"
                        gap="3"
                        cursor="pointer"
                        _hover={{ bg: "rgba(255, 255, 255, 0.08)" }}
                        transition="background 0.15s"
                        onClick={() => handleSelectResult(item)}
                      >
                        {itemImg ? (
                          <Image
                            src={itemImg}
                            alt={itemTitle}
                            boxSize="36px"
                            objectFit="cover"
                            rounded="md"
                            border="1px solid rgba(255,255,255,0.1)"
                            fallbackSrc="https://navneetlawstudio.com/logo.png"
                          />
                        ) : (
                          <Box p="2" bg="whiteAlpha.100" rounded="md">
                            <Icon as={FaBook} color="yellow.400" boxSize="16px" />
                          </Box>
                        )}
                        <Box flex="1" minW="0">
                          <Text fontSize="13px" fontWeight="600" color="white" noOfLines={1}>
                            {itemTitle}
                          </Text>
                          {itemAuthor && (
                            <Text fontSize="11px" color="gray.400">
                              {itemAuthor}
                            </Text>
                          )}
                        </Box>
                        {itemPrice && (
                          <Text fontSize="13px" fontWeight="700" color="yellow.400">
                            ₹{itemPrice}
                          </Text>
                        )}
                      </Flex>
                    );
                  })}
                  <Box
                    px="4"
                    py="2"
                    borderTop="1px solid rgba(255, 255, 255, 0.08)"
                    mt="1"
                    bg="rgba(0,0,0,0.15)"
                    textAlign="center"
                    cursor="pointer"
                    _hover={{ bg: "rgba(255, 255, 255, 0.05)" }}
                    onClick={() => handleExecuteSearch()}
                  >
                    <Text fontSize="12px" fontWeight="600" color="yellow.400">
                      View all results for "{query}" →
                    </Text>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Right Action Icons (Auth, Cart, Admin) */}
          <Flex align="center" gap={{ base: 2, md: 3 }}>
            {/* Account / Login Popover */}
            {isLoggedIn ? (
              <Popover trigger="hover" placement="bottom-end">
                <PopoverTrigger>
                  <Button
                    variant="ghost"
                    h="auto"
                    py="1.5"
                    px={{ base: 2, md: 3 }}
                    rounded="full"
                    bg="rgba(255, 255, 255, 0.08)"
                    _hover={{ bg: "rgba(255, 255, 255, 0.15)" }}
                    color="white"
                  >
                    <Flex align="center" gap="2">
                      <Box
                        w="28px"
                        h="28px"
                        rounded="full"
                        bg="yellow.500"
                        color="gray.900"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontWeight="bold"
                        fontSize="12px"
                      >
                        {username ? username.charAt(0).toUpperCase() : "U"}
                      </Box>
                      <Text
                        display={{ base: "none", sm: "block" }}
                        fontSize="13px"
                        fontWeight="600"
                        maxW="110px"
                        noOfLines={1}
                      >
                        {username}
                      </Text>
                    </Flex>
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  bg="#1E293B"
                  borderColor="rgba(255, 255, 255, 0.12)"
                  color="white"
                  rounded="xl"
                  shadow="0 20px 30px rgba(0,0,0,0.4)"
                  w="230px"
                  py="2"
                  _focus={{ boxShadow: "none" }}
                >
                  <PopoverArrow bg="#1E293B" />
                  <PopoverBody p="1">
                    <Box px="3" py="2" borderBottom="1px solid rgba(255,255,255,0.08)" mb="1">
                      <Text fontSize="11px" color="gray.400" textTransform="uppercase" fontWeight="bold">
                        Signed in as
                      </Text>
                      <Text fontSize="13px" fontWeight="bold" color="white" noOfLines={1}>
                        {username}
                      </Text>
                    </Box>

                    <NavLink to="/myprofile">
                      <Flex
                        align="center"
                        gap="3"
                        px="3"
                        py="2"
                        rounded="lg"
                        _hover={{ bg: "whiteAlpha.100", color: "yellow.400" }}
                        fontSize="13px"
                        fontWeight="500"
                        cursor="pointer"
                      >
                        <Icon as={FaUser} />
                        <Text>My Profile</Text>
                      </Flex>
                    </NavLink>

                    <NavLink to="/orderpage">
                      <Flex
                        align="center"
                        gap="3"
                        px="3"
                        py="2"
                        rounded="lg"
                        _hover={{ bg: "whiteAlpha.100", color: "yellow.400" }}
                        fontSize="13px"
                        fontWeight="500"
                        cursor="pointer"
                      >
                        <Icon as={RiInboxUnarchiveFill} />
                        <Text>My Orders</Text>
                      </Flex>
                    </NavLink>

                    {isAdminOrStaff && (
                      <NavLink to="/admin">
                        <Flex
                          align="center"
                          gap="3"
                          px="3"
                          py="2"
                          rounded="lg"
                          bg="rgba(234, 179, 8, 0.12)"
                          color="yellow.400"
                          _hover={{ bg: "rgba(234, 179, 8, 0.22)" }}
                          fontSize="13px"
                          fontWeight="bold"
                          cursor="pointer"
                          mt="1"
                        >
                          <Icon as={MdOutlineAdminPanelSettings} />
                          <Text>Admin Dashboard</Text>
                        </Flex>
                      </NavLink>
                    )}

                    <Box borderTop="1px solid rgba(255,255,255,0.08)" mt="1" pt="1">
                      <Flex
                        align="center"
                        gap="3"
                        px="3"
                        py="2"
                        rounded="lg"
                        _hover={{ bg: "red.500", color: "white" }}
                        color="red.300"
                        fontSize="13px"
                        fontWeight="500"
                        cursor="pointer"
                        onClick={handleLogout}
                      >
                        <Icon as={FaSignOutAlt} />
                        <Text>Log Out</Text>
                      </Flex>
                    </Box>
                  </PopoverBody>
                </PopoverContent>
              </Popover>
            ) : (
              <Link to="/login">
                <Button
                  variant="ghost"
                  h="auto"
                  py="2"
                  px={{ base: "3", md: "4" }}
                  rounded="full"
                  bg="yellow.500"
                  color="gray.950"
                  fontSize="13px"
                  fontWeight="700"
                  _hover={{ bg: "yellow.400", transform: "translateY(-1px)", shadow: "0 4px 12px rgba(234, 179, 8, 0.35)" }}
                  _active={{ transform: "translateY(0)" }}
                  transition="all 0.2s"
                >
                  Sign In
                </Button>
              </Link>
            )}

            {/* Cart Link with Modern Animated Badge */}
            <NavLink to="/cart">
              <Button
                variant="ghost"
                h="auto"
                py="2"
                px="3"
                rounded="full"
                bg="rgba(255, 255, 255, 0.08)"
                _hover={{ bg: "rgba(255, 255, 255, 0.15)", transform: "translateY(-1px)" }}
                transition="all 0.2s"
                position="relative"
                color="white"
                aria-label="Shopping Cart"
              >
                <Flex align="center" gap="2">
                  <Icon as={FaShoppingCart} boxSize="17px" color="yellow.400" />
                  <Text display={{ base: "none", md: "inline" }} fontSize="13px" fontWeight="600">
                    Cart
                  </Text>
                  {cartData.length > 0 && (
                    <Badge
                      position={{ base: "absolute", md: "static" }}
                      top={{ base: "-4px", md: "auto" }}
                      right={{ base: "-4px", md: "auto" }}
                      bg="red.500"
                      color="white"
                      fontSize="11px"
                      fontWeight="bold"
                      rounded="full"
                      px="1.5"
                      py="0.2"
                      boxShadow="0 0 10px rgba(239, 68, 68, 0.6)"
                    >
                      {cartData.length}
                    </Badge>
                  )}
                </Flex>
              </Button>
            </NavLink>
          </Flex>
        </Flex>

        {/* Mobile Search Bar (Below Main Nav) */}
        {!isLargerThan768 && (
          <Box mt="2.5" px="1">
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search legal books, bare acts, software..."
                value={query}
                onChange={handleInput}
                onKeyDown={handleKeyDown}
                bg="rgba(255, 255, 255, 0.08)"
                border="1px solid rgba(255, 255, 255, 0.12)"
                color="white"
                rounded="full"
                _placeholder={{ color: "gray.400" }}
                _focus={{
                  borderColor: "yellow.400",
                  bg: "rgba(15, 23, 42, 0.98)",
                }}
              />
            </InputGroup>
          </Box>
        )}
      </Box>

      {/* Mobile Drawer Menu */}
      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
        <DrawerContent bg="#0F172A" color="white" maxW="300px">
          <DrawerCloseButton color="gray.300" mt="2" />
          <DrawerHeader borderBottom="1px solid rgba(255,255,255,0.1)" py="4">
            <Flex align="center" gap="2.5">
              <Box p="1.5" bg="yellow.500" rounded="md" color="gray.950">
                <Icon as={FaGavel} boxSize="16px" />
              </Box>
              <Box>
                <Text fontSize="16px" fontWeight="bold" lineHeight="1.1">
                  Navneet Law Studios
                </Text>
                <Text fontSize="10px" color="gray.400" textTransform="uppercase">
                  Navigation Menu
                </Text>
              </Box>
            </Flex>
          </DrawerHeader>

          <DrawerBody px="3" py="4">
            {/* Mobile Search Input */}
            <Box mb="4">
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search books, bare acts, authors..."
                  value={query}
                  onChange={handleInput}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleExecuteSearch();
                      onClose();
                    }
                  }}
                  bg="rgba(255, 255, 255, 0.08)"
                  border="1px solid rgba(255, 255, 255, 0.15)"
                  color="white"
                  rounded="lg"
                  _placeholder={{ color: "gray.400" }}
                  _focus={{ borderColor: "yellow.400", bg: "rgba(15, 23, 42, 0.98)" }}
                />
              </InputGroup>
            </Box>

            {/* User status card */}
            <Box
              p="3"
              mb="4"
              rounded="xl"
              bg="rgba(255, 255, 255, 0.05)"
              border="1px solid rgba(255, 255, 255, 0.08)"
            >
              {isLoggedIn ? (
                <Flex align="center" gap="3">
                  <Box
                    w="34px"
                    h="34px"
                    rounded="full"
                    bg="yellow.500"
                    color="gray.950"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontWeight="bold"
                  >
                    {username ? username.charAt(0).toUpperCase() : "U"}
                  </Box>
                  <Box flex="1">
                    <Text fontSize="13px" fontWeight="bold" noOfLines={1}>
                      {username}
                    </Text>
                    <Text fontSize="11px" color="green.400">
                      ● Active Account
                    </Text>
                  </Box>
                </Flex>
              ) : (
                <Link to="/login" onClick={onClose} style={{ width: "100%" }}>
                  <Button
                    w="100%"
                    size="sm"
                    bg="yellow.500"
                    color="gray.950"
                    fontWeight="700"
                    rounded="lg"
                    py="2"
                    _hover={{ bg: "yellow.400" }}
                  >
                    Sign In / Register
                  </Button>
                </Link>
              )}
            </Box>

            {/* Menu Items */}
            <Flex direction="column" gap="1">
              <NavLink to="/" onClick={onClose}>
                <Flex
                  align="center"
                  gap="3"
                  px="3"
                  py="2.5"
                  rounded="lg"
                  _hover={{ bg: "whiteAlpha.100", color: "yellow.400" }}
                  fontSize="14px"
                  fontWeight="500"
                >
                  <Icon as={FaHome} color="yellow.400" />
                  <Text>Home</Text>
                </Flex>
              </NavLink>

              <NavLink to="/products?maincat=Books" onClick={onClose}>
                <Flex
                  align="center"
                  gap="3"
                  px="3"
                  py="2.5"
                  rounded="lg"
                  _hover={{ bg: "whiteAlpha.100", color: "yellow.400" }}
                  fontSize="14px"
                  fontWeight="500"
                >
                  <Icon as={FaBook} color="yellow.400" />
                  <Text>Book Store</Text>
                </Flex>
              </NavLink>

              <NavLink to="/products?maincat=Cloths-Accessories" onClick={onClose}>
                <Flex
                  align="center"
                  gap="3"
                  px="3"
                  py="2.5"
                  rounded="lg"
                  _hover={{ bg: "whiteAlpha.100", color: "yellow.400" }}
                  fontSize="14px"
                  fontWeight="500"
                >
                  <Text fontSize="16px">👔</Text>
                  <Text>Lawyers Clothing</Text>
                </Flex>
              </NavLink>

              <NavLink to="/software" onClick={onClose}>
                <Flex
                  align="center"
                  gap="3"
                  px="3"
                  py="2.5"
                  rounded="lg"
                  _hover={{ bg: "whiteAlpha.100", color: "yellow.400" }}
                  fontSize="14px"
                  fontWeight="500"
                >
                  <Text fontSize="16px">💻</Text>
                  <Text>Legal Software Solutions</Text>
                </Flex>
              </NavLink>

              <NavLink to="/cart" onClick={onClose}>
                <Flex
                  align="center"
                  gap="3"
                  px="3"
                  py="2.5"
                  rounded="lg"
                  _hover={{ bg: "whiteAlpha.100", color: "yellow.400" }}
                  fontSize="14px"
                  fontWeight="500"
                >
                  <Icon as={FaShoppingCart} color="yellow.400" />
                  <Text>My Cart</Text>
                  {cartData.length > 0 && (
                    <Badge ml="auto" colorScheme="red" rounded="full">
                      {cartData.length}
                    </Badge>
                  )}
                </Flex>
              </NavLink>

              <NavLink to="/orderpage" onClick={onClose}>
                <Flex
                  align="center"
                  gap="3"
                  px="3"
                  py="2.5"
                  rounded="lg"
                  _hover={{ bg: "whiteAlpha.100", color: "yellow.400" }}
                  fontSize="14px"
                  fontWeight="500"
                >
                  <Icon as={RiInboxUnarchiveFill} color="yellow.400" />
                  <Text>My Orders</Text>
                </Flex>
              </NavLink>

              <NavLink to="/myprofile" onClick={onClose}>
                <Flex
                  align="center"
                  gap="3"
                  px="3"
                  py="2.5"
                  rounded="lg"
                  _hover={{ bg: "whiteAlpha.100", color: "yellow.400" }}
                  fontSize="14px"
                  fontWeight="500"
                >
                  <Icon as={FaUser} color="yellow.400" />
                  <Text>My Profile</Text>
                </Flex>
              </NavLink>

              {isAdminOrStaff && (
                <NavLink to="/admin" onClick={onClose}>
                  <Flex
                    align="center"
                    gap="3"
                    px="3"
                    py="2.5"
                    rounded="lg"
                    bg="rgba(234, 179, 8, 0.15)"
                    color="yellow.400"
                    fontSize="14px"
                    fontWeight="bold"
                    mt="2"
                  >
                    <Icon as={MdOutlineAdminPanelSettings} />
                    <Text>Admin Dashboard</Text>
                  </Flex>
                </NavLink>
              )}

              <NavLink to="/contact" onClick={onClose}>
                <Flex
                  align="center"
                  gap="3"
                  px="3"
                  py="2.5"
                  rounded="lg"
                  _hover={{ bg: "whiteAlpha.100", color: "yellow.400" }}
                  fontSize="14px"
                  fontWeight="500"
                >
                  <Icon as={FaPhoneAlt} color="yellow.400" />
                  <Text>Contact Support</Text>
                </Flex>
              </NavLink>

              {isLoggedIn && (
                <Flex
                  align="center"
                  gap="3"
                  px="3"
                  py="2.5"
                  mt="3"
                  rounded="lg"
                  color="red.400"
                  _hover={{ bg: "red.500", color: "white" }}
                  fontSize="14px"
                  fontWeight="bold"
                  cursor="pointer"
                  onClick={() => {
                    handleLogout();
                    onClose();
                  }}
                >
                  <Icon as={FaSignOutAlt} />
                  <Text>Sign Out</Text>
                </Flex>
              )}
            </Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default Navbar;
